import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  Paper,
  Tooltip
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import RouteIcon from "@mui/icons-material/Route";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceIcon from "@mui/icons-material/Place";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import { GoogleMap, Marker, DirectionsRenderer, InfoWindow, Polyline } from "@react-google-maps/api";
import { getApps } from "firebase/app";
import { getDatabase, ref, onValue, off } from "firebase/database";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { api } from "common";
import { colors } from "../components/Theme/WebTheme";
import { FirebaseConfig } from "../config/FirebaseConfig";
import logoWhite from "../assets/img/logo138x75white.png";
import logoBlack from "../assets/img/logo138x75black.png";
import appstoreImg from "../assets/img/appstore.png";
import playstoreImg from "../assets/img/playstore.png";

const { formatPublicName } = api;

// Helper: Haversine distance in km
function calculateDistanceKm(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Helper: Decode Google Encoded Polyline algorithm into [{lat, lng}, ...]
function decodePolyline(encoded) {
  if (!encoded || typeof encoded !== "string") return [];
  const poly = [];
  let index = 0, len = encoded.length;
  let lat = 0, lng = 0;
  while (index < len) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
    lng += dlng;
    poly.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return poly;
}

// Helper: Detect client OS
const getClientOS = () => {
  if (typeof navigator === "undefined") return "desktop";
  const userAgent = navigator.userAgent || navigator.vendor || (typeof window !== "undefined" && window.opera) || "";
  if (/android/i.test(userAgent)) {
    return "android";
  }
  if (/iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
    return "ios";
  }
  return "desktop";
};

// Map styles
const mapContainerStyle = {
  width: "100%",
  height: "100%"
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  zoomControlOptions: {
    position: (typeof window !== "undefined" && window.google?.maps?.ControlPosition?.RIGHT_BOTTOM) || 9
  },
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  fullscreenControlOptions: {
    position: (typeof window !== "undefined" && window.google?.maps?.ControlPosition?.LEFT_TOP) || 5
  },
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }]
    }
  ]
};

export default function LiveTrackTrip() {
  const { t } = useTranslation();
  const clientOS = getClientOS();
  const { token } = useParams();
  const settings = useSelector((state) => state.settingsdata?.settings || {});
  const appleStoreUrl = settings?.AppleStoreLink || "https://apps.apple.com/app";
  const playStoreUrl = settings?.PlayStoreLink || "https://play.google.com/store/apps";
  const companyWebsite = settings?.CompanyWebsite || "/";
  const appName = settings?.appName || "INDRIVING";
  const logoWhiteUrl = settings?.CompanyLogo || settings?.logo || logoWhite;
  const logoBlackUrl = settings?.CompanyLogo || settings?.logo || logoBlack;

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [directions, setDirections] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [eta, setEta] = useState(null);
  const [remainingDistance, setRemainingDistance] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const mapRef = useRef(null);
  const directionsRequestedRef = useRef(false);
  const boundsFittedRef = useRef(false);

  const carImageUrl = trip?.carImage || "https://maps.google.com/mapfiles/kml/shapes/cabs.png";
  const [carIconSize, setCarIconSize] = useState({ width: 60, height: 26 });

  // Calculate natural aspect ratio of car image to prevent lengthwise compression
  useEffect(() => {
    if (carImageUrl) {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          const aspect = img.naturalWidth / img.naturalHeight;
          const targetHeight = 26;
          const targetWidth = Math.round(targetHeight * aspect);
          setCarIconSize({
            width: Math.max(Math.min(targetWidth, 90), 26),
            height: targetHeight
          });
        }
      };
      img.src = carImageUrl;
    }
  }, [carImageUrl]);

  // Set document title dynamically
  useEffect(() => {
    const appName = settings?.appName || "INDRIVING";
    if (trip?.driverName) {
      document.title = `Track Trip - ${formatPublicName(trip.driverName)} | ${appName}`;
    } else {
      document.title = `Live Trip Tracking | ${appName}`;
    }
  }, [trip, settings]);

  // 1. Listen to Realtime Database at /shared_trips/{token} with REST fallback
  useEffect(() => {
    if (!token) {
      setError("No tracking token provided.");
      setLoading(false);
      return;
    }

    let isMounted = true;
    let pollInterval = null;

    const dbUrl = (FirebaseConfig && FirebaseConfig.databaseURL)
      ? FirebaseConfig.databaseURL.replace(/\/+$/, '')
      : `https://${FirebaseConfig.projectId}-default-rtdb.firebaseio.com`;

    // Direct REST fetch: fast, reliable, bypasses WebSocket connection issues
    const fetchViaRest = async () => {
      try {
        const res = await fetch(`${dbUrl}/shared_trips/${token}.json`);
        if (res.ok) {
          const data = await res.json();
          if (isMounted) {
            if (data && (data.bookingId || data.shareToken)) {
              setTrip(data);
              setError(null);
              setLoading(false);
              return data;
            } else {
              setError("Tracking link not found or expired.");
              setTrip(null);
              setLoading(false);
            }
          }
        }
      } catch (err) {
        console.error("REST fetch error:", err);
      }
      return null;
    };

    // Immediate initial fetch via REST
    fetchViaRest();

    // Setup periodic polling safety net every 4 seconds
    pollInterval = setInterval(() => {
      fetchViaRest();
    }, 4000);

    // Setup Firebase Realtime Listener with explicitly matched databaseURL
    let tripRef = null;
    try {
      const apps = getApps();
      const app = apps.length > 0 ? apps[0] : undefined;
      const db = getDatabase(app, dbUrl);
      tripRef = ref(db, `shared_trips/${token}`);

      onValue(
        tripRef,
        (snapshot) => {
          if (!isMounted) return;
          const val = snapshot.val();
          if (val && (val.bookingId || val.shareToken)) {
            setTrip(val);
            setError(null);
            setLoading(false);
          } else if (!val) {
            // Verify with REST before concluding it is expired
            fetchViaRest();
          }
        },
        (err) => {
          console.error("Error reading shared trip:", err);
          if (isMounted) {
            fetchViaRest();
          }
        }
      );
    } catch (e) {
      console.error("Firebase connection error:", e);
      fetchViaRest();
    }

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
      if (tripRef) {
        try {
          off(tripRef);
        } catch (e) {}
      }
    };
  }, [token]);

  // 2. Compute live ETA and Remaining Distance
  useEffect(() => {
    if (!trip) return;

    const currentLoc = trip.currentLocation || (trip.pickup ? { lat: trip.pickup.lat, lng: trip.pickup.lng } : null);
    if (!currentLoc || !currentLoc.lat || !currentLoc.lng) return;

    // Target depends on status: if ACCEPTED, target is pickup. If STARTED/ARRIVED/REACHED, target is drop.
    const isHeadingToPickup = trip.status === "ACCEPTED";
    const targetLoc = isHeadingToPickup ? trip.pickup : trip.drop;

    if (targetLoc && targetLoc.lat && targetLoc.lng) {
      const distKm = calculateDistanceKm(currentLoc.lat, currentLoc.lng, targetLoc.lat, targetLoc.lng);
      setRemainingDistance(distKm.toFixed(1) + " km");

      // Approximate city travel speed: 25 km/h -> mins = (dist / 25) * 60
      const approxMins = Math.max(1, Math.round((distKm / 25) * 60));
      setEta(`${approxMins} min${approxMins > 1 ? "s" : ""}`);
    }
  }, [trip]);

  // 3. Clear route polyline if trip finishes
  useEffect(() => {
    if (!trip) return;
    const isFinished = !trip.active || trip.status === "COMPLETE" || trip.status === "CANCELLED";
    if (isFinished) {
      setDirections(null);
      setRouteCoordinates([]);
    }
  }, [trip]);

  // 4. Fetch Route Polyline & Directions (only for active trips)
  useEffect(() => {
    if (!trip || !trip.pickup || !trip.drop) return;
    const isFinished = !trip.active || trip.status === "COMPLETE" || trip.status === "CANCELLED";
    if (isFinished) return;
    if (directionsRequestedRef.current) return;
    directionsRequestedRef.current = true;

    const pLat = Number(trip.pickup.lat);
    const pLng = Number(trip.pickup.lng);
    const dLat = Number(trip.drop.lat);
    const dLng = Number(trip.drop.lng);

    if (isNaN(pLat) || isNaN(pLng) || isNaN(dLat) || isNaN(dLng)) return;

    // Check if trip already contains polyline points (e.g. from booking)
    if (trip.polylinePoints) {
      const decoded = decodePolyline(trip.polylinePoints);
      if (decoded && decoded.length > 0) {
        setRouteCoordinates(decoded);
      }
    }

    // Immediate fallback straight line so a polyline is NEVER missing
    const immediateFallback = [
      { lat: pLat, lng: pLng },
      ...(trip.waypoints && Array.isArray(trip.waypoints)
        ? trip.waypoints.map((w) => ({ lat: Number(w.lat), lng: Number(w.lng) }))
        : []),
      { lat: dLat, lng: dLng }
    ];
    setRouteCoordinates((prev) => (prev && prev.length > 0 ? prev : immediateFallback));

    // A. Fetch accurate turn-by-turn road polyline from backend /googleapi
    const fetchRouteFromBackend = async () => {
      try {
        const body = {
          start: `${pLat},${pLng}`,
          dest: `${dLat},${dLng}`,
          calltype: "direction"
        };
        if (trip.waypoints && Array.isArray(trip.waypoints) && trip.waypoints.length > 0) {
          body.waypoints = trip.waypoints.map((w) => `${w.lat},${w.lng}`).join("|");
        }

        const endpoints = [
          "/googleapi",
          `https://${FirebaseConfig.projectId || "sride-777"}.web.app/googleapi`
        ];

        const authHeader =
          "Basic " +
          btoa(
            (FirebaseConfig.projectId || "sride-777") +
              ":U2FsdGVkX19cpgyWxP3Ow6XtiMpHpEuXi7qGmi1Wb4H0l2Vx1k1uGjYkBYcG6I3dEek2k1st48ADUdL69HtBBQ=="
          );

        for (const endpoint of endpoints) {
          try {
            const res = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: authHeader
              },
              body: JSON.stringify(body)
            });
            if (res.ok) {
              const data = await res.json();
              if (data && data.polylinePoints) {
                const decoded = decodePolyline(data.polylinePoints);
                if (decoded && decoded.length > 0) {
                  setRouteCoordinates(decoded);
                }
                if (data.distance_in_km) {
                  setRemainingDistance(Number(data.distance_in_km).toFixed(1) + " km");
                }
                if (data.time_in_secs) {
                  const mins = Math.max(1, Math.round(Number(data.time_in_secs) / 60));
                  setEta(`${mins} min${mins > 1 ? "s" : ""}`);
                }
                break;
              }
            }
          } catch (e) {
            console.warn(`Fetch error from ${endpoint}:`, e);
          }
        }
      } catch (err) {
        console.warn("Backend route fetch error:", err);
      }
    };

    fetchRouteFromBackend();

    // B. Also attempt client DirectionsService if available
    const tryDirectionsService = () => {
      if (typeof window !== "undefined" && window.google && window.google.maps && window.google.maps.DirectionsService) {
        try {
          const directionsService = new window.google.maps.DirectionsService();
          directionsService.route(
            {
              origin: { lat: pLat, lng: pLng },
              destination: { lat: dLat, lng: dLng },
              travelMode: window.google.maps.TravelMode.DRIVING
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                setDirections(result);
                if (result.routes[0]?.legs[0]?.duration?.text) {
                  setEta(result.routes[0].legs[0].duration.text);
                }
                if (result.routes[0]?.legs[0]?.distance?.text) {
                  setRemainingDistance(result.routes[0].legs[0].distance.text);
                }
              }
            }
          );
        } catch (e) {
          console.warn("DirectionsService error:", e);
        }
      }
    };

    tryDirectionsService();
  }, [trip]);

  // Retry DirectionsService if map loads later
  useEffect(() => {
    if (!mapLoaded || !trip || directions) return;
    if (typeof window !== "undefined" && window.google && window.google.maps && window.google.maps.DirectionsService) {
      try {
        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin: { lat: Number(trip.pickup.lat), lng: Number(trip.pickup.lng) },
            destination: { lat: Number(trip.drop.lat), lng: Number(trip.drop.lng) },
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              setDirections(result);
            }
          }
        );
      } catch (e) {}
    }
  }, [mapLoaded, trip, directions]);

  // Fit map bounds to encompass pickup, drop, vehicle, and route polyline
  const fitTripBounds = useCallback(() => {
    if (!mapRef.current || typeof window === "undefined" || !window.google || !window.google.maps) return;
    try {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;

      if (routeCoordinates && routeCoordinates.length > 0) {
        const step = Math.max(1, Math.floor(routeCoordinates.length / 25));
        for (let i = 0; i < routeCoordinates.length; i += step) {
          bounds.extend(new window.google.maps.LatLng(routeCoordinates[i].lat, routeCoordinates[i].lng));
          hasPoints = true;
        }
        bounds.extend(
          new window.google.maps.LatLng(
            routeCoordinates[routeCoordinates.length - 1].lat,
            routeCoordinates[routeCoordinates.length - 1].lng
          )
        );
      }

      if (trip?.pickup?.lat && trip?.pickup?.lng) {
        bounds.extend(new window.google.maps.LatLng(Number(trip.pickup.lat), Number(trip.pickup.lng)));
        hasPoints = true;
      }
      if (trip?.drop?.lat && trip?.drop?.lng) {
        bounds.extend(new window.google.maps.LatLng(Number(trip.drop.lat), Number(trip.drop.lng)));
        hasPoints = true;
      }
      if (trip?.currentLocation?.lat && trip?.currentLocation?.lng) {
        bounds.extend(new window.google.maps.LatLng(Number(trip.currentLocation.lat), Number(trip.currentLocation.lng)));
        hasPoints = true;
      }

      if (hasPoints) {
        mapRef.current.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });
      }
    } catch (e) {
      console.warn("Could not fit bounds:", e);
    }
  }, [trip, routeCoordinates]);

  // Initial bounds fitting once map and coordinates/trip are ready
  useEffect(() => {
    if (!mapLoaded || !trip) return;
    if (boundsFittedRef.current) return;
    boundsFittedRef.current = true;
    fitTripBounds();
  }, [mapLoaded, trip, routeCoordinates, fitTripBounds]);

  // Map onLoad callback
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);

  // Recenter map on vehicle
  const recenterOnVehicle = () => {
    if (!mapRef.current || !trip?.currentLocation) return;
    mapRef.current.panTo({
      lat: Number(trip.currentLocation.lat),
      lng: Number(trip.currentLocation.lng)
    });
    mapRef.current.setZoom(16);
  };

  // Status badge config
  const getStatusBadge = (status, active) => {
    if (!active || status === "COMPLETE") {
      return {
        label: t('trip_completed', 'Trip Completed'),
        color: "#2e7d32",
        bg: "#e8f5e9",
        icon: <CheckCircleIcon style={{ fontSize: 18, color: "#2e7d32" }} />,
        desc: t('ride_concluded_safely', 'The ride has concluded safely.')
      };
    }
    if (status === "CANCELLED") {
      return {
        label: t('trip_cancelled', 'Trip Cancelled'),
        color: "#c62828",
        bg: "#ffebee",
        icon: <CancelIcon style={{ fontSize: 18, color: "#c62828" }} />,
        desc: t('ride_was_cancelled', 'This ride was cancelled.')
      };
    }
    if (status === "ACCEPTED") {
      return {
        label: t('driver_on_the_way', 'Driver on the way'),
        color: "#1565c0",
        bg: "#e3f2fd",
        icon: <DirectionsCarIcon style={{ fontSize: 18, color: "#1565c0" }} />,
        desc: t('driver_heading_to_pickup', 'Driver is heading to pickup point')
      };
    }
    if (status === "ARRIVED") {
      return {
        label: t('driver_arrived_pickup', 'Driver Arrived at Pickup'),
        color: "#ef6c00",
        bg: "#fff3e0",
        icon: <PlaceIcon style={{ fontSize: 18, color: "#ef6c00" }} />,
        desc: t('driver_waiting_pickup', 'Driver is waiting at pickup location')
      };
    }
    if (status === "STARTED") {
      return {
        label: t('trip_in_progress', 'Trip in Progress'),
        color: "#2e7d32",
        bg: "#e8f5e9",
        icon: <RadioButtonCheckedIcon style={{ fontSize: 18, color: "#2e7d32" }} />,
        desc: t('heading_to_destination', 'Heading toward destination')
      };
    }
    if (status === "REACHED") {
      return {
        label: t('arriving_at_destination', 'Arriving at Destination'),
        color: "#6a1b9a",
        bg: "#f3e5f5",
        icon: <PlaceIcon style={{ fontSize: 18, color: "#6a1b9a" }} />,
        desc: t('driver_close_destination', 'Driver is close to destination')
      };
    }
    return {
      label: status ? t(status, status) : t('in_progress', 'In Progress'),
      color: "#333",
      bg: "#f5f5f5",
      icon: <DirectionsCarIcon style={{ fontSize: 18, color: "#333" }} />,
      desc: t('live_tracking_active', 'Live tracking active')
    };
  };

  // Loading view
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="#f8fafc"
        p={3}
      >
        <Box mb={3} textAlign="center">
          <img
            src={logoBlackUrl}
            alt={appName}
            style={{ height: 50, width: "auto", maxWidth: 180, objectFit: "contain" }}
          />
        </Box>
        <CircularProgress size={50} style={{ color: colors.MAIN_COLOR || "#2e3c25" }} />
        <Typography variant="h6" mt={3} color="textSecondary" fontWeight="600">
          Connecting to Live Trip Tracking...
        </Typography>
        <Typography variant="body2" color="textSecondary" mt={1}>
          Loading real-time vehicle coordinates and trip status
        </Typography>
      </Box>
    );
  }

  // Error, Not Found, or Completed/Expired Trip view
  const isTripFinished = !trip || !trip.active || trip.status === "COMPLETE" || trip.status === "CANCELLED";

  if (error || isTripFinished) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="#f8fafc"
        p={3}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            maxWidth: 480,
            width: "100%",
            borderRadius: 4,
            textAlign: "center",
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)"
          }}
        >
          {/* Company Logo */}
          <Box mb={2.5} display="flex" justifyContent="center">
            <a href={companyWebsite} target="_blank" rel="noopener noreferrer">
              <img
                src={logoBlackUrl}
                alt={appName}
                style={{ height: 48, width: "auto", maxWidth: 180, objectFit: "contain" }}
              />
            </a>
          </Box>

          <CancelIcon style={{ fontSize: 56, color: "#ef4444", marginBottom: 16 }} />
          <Typography variant="h5" fontWeight="700" gutterBottom color="#1e293b">
            {t('tracking_link_expired', 'Tracking Link Expired')}
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {trip && (trip.status === "COMPLETE" || trip.status === "CANCELLED" || !trip.active)
              ? t('trip_completed_expired', "This trip has completed safely. Live tracking has ended.")
              : (error || t('trip_link_expired', "This tracking link has expired or does not exist."))}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" mb={2.5}>
            {t('tracking_link_expired_desc', 'For security and privacy, live tracking links are strictly temporary and automatically expire once the ride is completed.')}
          </Typography>

          {/* Links (Buttons) to App in App Stores */}
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="body2" color="#334155" fontWeight="600" align="center" mb={2}>
            {(() => {
              const promoText = t('download_app_promo', { appName, defaultValue: `Download ${appName} to book rides & track trips on your smartphone` });
              return (promoText || '')
                .replace(/\{\{appName\}\}/g, appName)
                .replace(/\{appName\}/g, appName);
            })()}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1.5}
            flexWrap="wrap"
          >
            {(clientOS === "desktop" || clientOS === "ios") && appleStoreUrl && (
              <a
                href={appleStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-block", transition: "transform 0.2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <img
                  src={appstoreImg}
                  alt="Download on the App Store"
                  style={{ height: 40, width: "auto", display: "block" }}
                />
              </a>
            )}
            {(clientOS === "desktop" || clientOS === "android") && playStoreUrl && (
              <a
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-block", transition: "transform 0.2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <img
                  src={playstoreImg}
                  alt="GET IT ON Google Play"
                  style={{ height: 40, width: "auto", display: "block" }}
                />
              </a>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }
  const statusInfo = getStatusBadge(trip.status, trip.active);

  const carPos = trip.currentLocation && trip.currentLocation.lat && trip.currentLocation.lng
    ? { lat: Number(trip.currentLocation.lat), lng: Number(trip.currentLocation.lng) }
    : trip.pickup
    ? { lat: Number(trip.pickup.lat), lng: Number(trip.pickup.lng) }
    : { lat: 0, lng: 0 };

  const pickupPos = trip.pickup && trip.pickup.lat ? { lat: Number(trip.pickup.lat), lng: Number(trip.pickup.lng) } : null;
  const dropPos = trip.drop && trip.drop.lat ? { lat: Number(trip.drop.lat), lng: Number(trip.drop.lng) } : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: { xs: "100dvh", md: "100vh" },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        overflowY: "hidden",
        bgcolor: "#f1f5f9",
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* LEFT SIDE: Information & Driver Panel */}
      <Box
        sx={{
          width: { xs: "100%", md: 440 },
          maxWidth: { md: 460 },
          height: { xs: "auto", md: "100vh" },
          maxHeight: { xs: "50vh", md: "100vh" },
          overflowY: "auto",
          bgcolor: "#ffffff",
          boxShadow: { xs: "0 4px 20px rgba(0,0,0,0.1)", md: "4px 0 24px rgba(0,0,0,0.08)" },
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          order: { xs: 2, md: 1 }
        }}
      >
        {/* Brand Header */}
        <Box
          sx={{
            p: 2,
            px: 2.5,
            bgcolor: colors.MAIN_COLOR || "#2e3c25",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255,255,255,0.12)"
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <a
              href={companyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
            >
              <img
                src={logoWhiteUrl}
                alt={appName}
                style={{
                  height: 38,
                  width: "auto",
                  maxWidth: 130,
                  objectFit: "contain",
                  display: "block"
                }}
              />
            </a>
            <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.25)", pl: 1.5, ml: 0.5 }}>
              <Typography variant="subtitle2" fontWeight="700" letterSpacing={0.5} lineHeight={1.2}>
                {appName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, fontSize: "0.72rem" }}>
                {t('live_trip_tracking', 'Live Trip Tracking')}
              </Typography>
            </Box>
          </Box>

          {!isTripFinished && (
            <Chip
              size="small"
              icon={<RadioButtonCheckedIcon style={{ fontSize: 12, color: "#22c55e" }} />}
              label="LIVE"
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontWeight: "700",
                fontSize: "0.7rem",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.2)"
              }}
            />
          )}
        </Box>

        <Box sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Live Status Pill */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: statusInfo.bg,
              border: `1px solid ${statusInfo.color}30`,
              display: "flex",
              alignItems: "center",
              gap: 1.5
            }}
          >
            {statusInfo.icon}
            <Box>
              <Typography variant="subtitle2" fontWeight="700" sx={{ color: statusInfo.color }}>
                {statusInfo.label}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {statusInfo.desc}
              </Typography>
            </Box>
          </Paper>

          {/* Trip Completed Alert Banner */}
          {isTripFinished && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: trip.status === "COMPLETE" ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${trip.status === "COMPLETE" ? "#86efac" : "#fca5a5"}`,
                textAlign: "center"
              }}
            >
              {trip.status === "COMPLETE" ? (
                <>
                  <CheckCircleIcon sx={{ fontSize: 40, color: "#16a34a", mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="700" color="#166534">
                    {t('trip_completed_safely', 'Trip Completed Safely')}
                  </Typography>
                  <Typography variant="body2" color="#15803d" mt={0.5}>
                    {trip.passengerName ? `${formatPublicName(trip.passengerName)} has` : "Passenger has"} reached the destination. Live tracking has ended.
                  </Typography>
                </>
              ) : (
                <>
                  <CancelIcon sx={{ fontSize: 40, color: "#dc2626", mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="700" color="#991b1b">
                    {t('trip_was_cancelled', 'Trip Was Cancelled')}
                  </Typography>
                  <Typography variant="body2" color="#b91c1c" mt={0.5}>
                    This ride was cancelled. Live tracking is no longer active.
                  </Typography>
                </>
              )}
            </Paper>
          )}

          {/* Live ETA & Distance Card */}
          {!isTripFinished && eta && (
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: `${colors.MAIN_COLOR || "#2e3c25"}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.MAIN_COLOR || "#2e3c25"
                  }}
                >
                  <AccessTimeIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight="600" textTransform="uppercase">
                    {t('estimated_arrival', 'Estimated Arrival')}
                  </Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a">
                    {eta}
                  </Typography>
                </Box>
              </Box>
              {remainingDistance && (
                <Chip
                  label={remainingDistance}
                  size="small"
                  sx={{
                    bgcolor: "#e2e8f0",
                    color: "#334155",
                    fontWeight: "700",
                    fontSize: "0.75rem",
                    borderRadius: 2
                  }}
                />
              )}
            </Card>
          )}

          {/* Driver & Vehicle Information Card */}
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
          >
            <Typography variant="caption" color="textSecondary" fontWeight="700" letterSpacing={0.5} textTransform="uppercase" display="block" mb={1.5}>
              {t('driver_vehicle_info', 'Driver & Vehicle Info')}
            </Typography>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={trip.driverImage || undefined}
                  alt={formatPublicName(trip.driverName) || "Driver"}
                  sx={{ width: 56, height: 56, bgcolor: colors.MAIN_COLOR || "#2e3c25", border: "2px solid #e2e8f0" }}
                >
                  {trip.driverName ? formatPublicName(trip.driverName).charAt(0) : "D"}
                </Avatar>

                <Box>
                  <Typography variant="subtitle1" fontWeight="700" color="#0f172a" lineHeight={1.2}>
                    {formatPublicName(trip.driverName) || t('assigned_driver', { defaultValue: 'Assigned Driver' })}
                  </Typography>
                  {trip.driverRating && (
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                      <StarIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography variant="body2" fontWeight="700" color="#0f172a">
                        {trip.driverRating}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        rating
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Vehicle Type Image / Icon with Vehicle Type text */}
              <Box display="flex" flexDirection="column" alignItems="center">
                {trip.carImage ? (
                  <img
                    src={trip.carImage}
                    alt={trip.carType || "Vehicle"}
                    style={{ width: 70, height: 38, objectFit: "contain" }}
                  />
                ) : (
                  <DirectionsCarIcon sx={{ fontSize: 38, color: colors.SECONDARY || "#85bb65" }} />
                )}
                <Typography variant="body2" fontWeight="700" color="#1e293b" mt={0.5} align="center">
                  {trip.carType || "Vehicle"}
                </Typography>
              </Box>
            </Box>

            {/* Redesigned Single-Line Vehicle Block */}
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1
              }}
            >
              <Typography
                variant="body1"
                fontWeight="700"
                color="#1e293b"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {[
                  trip.vehicleColor || trip.vehicle_color || trip.carColor || trip.car_color,
                  trip.vehicleMake || trip.vehicle_make,
                  trip.vehicleModel || trip.vehicle_model
                ].filter(Boolean).join("  ") || trip.carType || t('car', { defaultValue: 'Car' })}
              </Typography>
              {(trip.vehicleNumber || trip.vehicle_number) && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    bgcolor: "#fef9c3",
                    border: "1px solid #ca8a04",
                    flexShrink: 0
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="800"
                    letterSpacing={1}
                    color="#854d0e"
                    sx={{ fontFamily: "'Roboto Mono', monospace" }}
                  >
                    {trip.vehicleNumber || trip.vehicle_number}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>

          {/* Route Details: Pickup & Drop */}
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
          >
            <Typography variant="caption" color="textSecondary" fontWeight="700" letterSpacing={0.5} textTransform="uppercase" display="block" mb={1.5}>
              {t('trip_route', 'Trip Route')}
            </Typography>

            {/* Pickup */}
            <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "#dcfce7",
                  color: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 0.25,
                  flexShrink: 0
                }}
              >
                <RadioButtonCheckedIcon sx={{ fontSize: 14 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight="600">
                  {t('pickup', 'PICKUP')}
                </Typography>
                <Typography variant="body2" fontWeight="600" color="#1e293b">
                  {trip.pickup?.add || "Pickup location"}
                </Typography>
              </Box>
            </Box>

            {/* Destination */}
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 0.25,
                  flexShrink: 0
                }}
              >
                <PlaceIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight="600">
                  {t('destination', 'DESTINATION')}
                </Typography>
                <Typography variant="body2" fontWeight="600" color="#1e293b">
                  {trip.drop?.add || "Destination address"}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Mobile App Download Card */}
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              textAlign: "center"
            }}
          >
            <Typography variant="body2" color="#334155" fontWeight="600" mb={1.5}>
              {(() => {
                const promoText = t('download_app_promo', { appName, defaultValue: `Download ${appName} to book rides & track trips on your smartphone` });
                return (promoText || '')
                  .replace(/\{\{appName\}\}/g, appName)
                  .replace(/\{appName\}/g, appName);
              })()}
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1.5}
              flexWrap="wrap"
            >
              {(clientOS === "desktop" || clientOS === "ios") && appleStoreUrl && (
                <a
                  href={appleStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-block", transition: "transform 0.2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                  <img
                    src={appstoreImg}
                    alt="Download on the App Store"
                    style={{ height: 40, width: "auto", display: "block" }}
                  />
                </a>
              )}
              {(clientOS === "desktop" || clientOS === "android") && playStoreUrl && (
                <a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-block", transition: "transform 0.2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                  <img
                    src={playstoreImg}
                    alt="GET IT ON Google Play"
                    style={{ height: 40, width: "auto", display: "block" }}
                  />
                </a>
              )}
            </Box>
          </Card>

          {/* Passenger Sharing Notice */}
          <Box
            sx={{
              mt: "auto",
              p: 1.5,
              borderRadius: 2,
              bgcolor: "#f8fafc",
              border: "1px dashed #cbd5e1",
              textAlign: "center"
            }}
          >
            <Typography variant="caption" color="textSecondary">
              {(() => {
                const rawPassenger =
                  trip?.passengerName ||
                  trip?.passenger_name ||
                  trip?.customer_name ||
                  trip?.customerName ||
                  (trip?.passenger && trip.passenger.name) ||
                  "";
                const passengerDisplay = formatPublicName(rawPassenger) || rawPassenger || t('passenger', 'Passenger');
                const noticeText = t('shared_by_passenger', {
                  passenger: passengerDisplay,
                  defaultValue: `Shared by ${passengerDisplay} for real-time safety and peace of mind.`
                });
                return (noticeText || "")
                  .replace(/\{\{passenger\}\}/g, passengerDisplay)
                  .replace(/\{passenger\}/g, passengerDisplay);
              })()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* RIGHT SIDE: Interactive Google Map */}
      <Box
        sx={{
          flex: 1,
          height: { xs: "50vh", md: "100vh" },
          minHeight: { xs: 320, md: "auto" },
          position: "relative",
          order: { xs: 1, md: 2 },
          overflow: "hidden",
          "& .gm-fullscreen-control": {
            marginLeft: "14px !important",
            marginTop: "12px !important"
          },
          "& .gm-bundled-control": {
            marginRight: "14px !important",
            marginBottom: "14px !important"
          }
        }}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={carPos}
          zoom={15}
          onLoad={onMapLoad}
          options={mapOptions}
        >
          {/* Route Directions: DirectionsRenderer or Decoded Polyline (only while trip is active) */}
          {!isTripFinished && (
            directions ? (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  preserveViewport: true,
                  polylineOptions: {
                    strokeColor: colors.MAIN_COLOR || "#2e3c25",
                    strokeWeight: 6,
                    strokeOpacity: 0.9,
                    zIndex: 50
                  }
                }}
              />
            ) : routeCoordinates && routeCoordinates.length > 0 ? (
              <>
                {/* Outer stroke for contrast & depth */}
                <Polyline
                  path={routeCoordinates}
                  options={{
                    strokeColor: "#1e293b",
                    strokeWeight: 8,
                    strokeOpacity: 0.35,
                    geodesic: true,
                    zIndex: 49
                  }}
                />
                {/* Primary route polyline */}
                <Polyline
                  path={routeCoordinates}
                  options={{
                    strokeColor: colors.MAIN_COLOR || "#2e3c25",
                    strokeWeight: 5,
                    strokeOpacity: 0.95,
                    geodesic: true,
                    zIndex: 50
                  }}
                />
              </>
            ) : null
          )}

          {/* Pickup Marker */}
          {pickupPos && (
            <Marker
              position={pickupPos}
              onClick={() => setSelectedMarker("pickup")}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png"
              }}
            >
              {selectedMarker === "pickup" && (
                <InfoWindow onCloseClick={() => setSelectedMarker(null)}>
                  <Box p={0.5}>
                    <Typography variant="subtitle2" fontWeight="700" color="#16a34a">
                      {t('pickup_location', 'Pickup Location')}
                    </Typography>
                    <Typography variant="body2">{trip.pickup?.add}</Typography>
                  </Box>
                </InfoWindow>
              )}
            </Marker>
          )}

          {/* Drop Marker */}
          {dropPos && (
            <Marker
              position={dropPos}
              onClick={() => setSelectedMarker("drop")}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png"
              }}
            >
              {selectedMarker === "drop" && (
                <InfoWindow onCloseClick={() => setSelectedMarker(null)}>
                  <Box p={0.5}>
                    <Typography variant="subtitle2" fontWeight="700" color="#dc2626">
                      {t('drop_location', 'Drop Location')}
                    </Typography>
                    <Typography variant="body2">{trip.drop?.add}</Typography>
                  </Box>
                </InfoWindow>
              )}
            </Marker>
          )}

          {/* Moving Vehicle Marker */}
          {!isTripFinished && carPos.lat !== 0 && (
            <Marker
              position={carPos}
              onClick={() => setSelectedMarker("car")}
              icon={{
                url: carImageUrl,
                scaledSize:
                  typeof window !== "undefined" && window.google && window.google.maps
                    ? new window.google.maps.Size(carIconSize.width, carIconSize.height)
                    : undefined,
                anchor:
                  typeof window !== "undefined" && window.google && window.google.maps
                    ? new window.google.maps.Point(carIconSize.width / 2, carIconSize.height / 2)
                    : undefined
              }}
            >
              {selectedMarker === "car" && (
                <InfoWindow onCloseClick={() => setSelectedMarker(null)}>
                  <Box p={0.5}>
                    <Typography variant="subtitle2" fontWeight="700">
                      {formatPublicName(trip.driverName) || "Driver"}
                    </Typography>
                    <Typography variant="body2">{trip.vehicleNumber}</Typography>
                    {eta && (
                      <Typography variant="caption" color="textSecondary">
                        ETA: {eta}
                      </Typography>
                    )}
                  </Box>
                </InfoWindow>
              )}
            </Marker>
          )}
        </GoogleMap>

        {/* Floating Map Action Buttons */}
        {!isTripFinished && (
          <Box
            sx={{
              position: "absolute",
              top: { xs: 12, md: 20 },
              right: { xs: 14, md: 20 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1, md: 1.5 },
              zIndex: 10
            }}
          >
            {/* Recenter on Vehicle */}
            <Tooltip title={t('focus_on_vehicle', 'Focus on Vehicle')} placement="left">
              <IconButton
                onClick={recenterOnVehicle}
                sx={{
                  bgcolor: "#ffffff",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
                  color: colors.MAIN_COLOR || "#2e3c25",
                  "&:hover": { bgcolor: "#f8fafc" },
                  width: { xs: 40, md: 46 },
                  height: { xs: 40, md: 46 }
                }}
              >
                <MyLocationIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
              </IconButton>
            </Tooltip>

            {/* View Full Route Bounds */}
            <Tooltip title={t('view_entire_route', 'View Entire Route')} placement="left">
              <IconButton
                onClick={fitTripBounds}
                sx={{
                  bgcolor: "#ffffff",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
                  color: colors.MAIN_COLOR || "#2e3c25",
                  "&:hover": { bgcolor: "#f8fafc" },
                  width: { xs: 40, md: 46 },
                  height: { xs: 40, md: 46 }
                }}
              >
                <RouteIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
}
